import { useEffect } from "react";
import { DevLanguageSelector } from "../../basic/devLanguageSelector/DevLanguageSelector";
import AnonymousHandoffFeedback from "../anonymousAccountFlow/AnonymousHandoffFeedback";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useRTContext } from "../../context/RTContext";
import { useTestCreationContext } from "../../context/TestCreationContext";
import { checkHasCompletedProfile } from "../../helpers/profileCompletion";
import { useOnboardingAnimation } from "../../hooks/useOnboardingAnimation";
import { trackTouchpointPageView } from "../../networking/touchpoint";
import ContentWithHeader from "../../wrapper/ContentWithHeader/ContentWithHeader";
import { AssistantContentSection } from "./AssistantContentSection";
import { AssistantIntroSection } from "./AssistantIntroSection";

export const AssistantOverview = () => {
    const { teacherInfo, userInfo, getAndSetUserAndTeacherInfo, hasVocabTraining, hasTest } = useGeneralContext();
    const { userTests, dataLoaded: testDataLoaded, userTestsLoadError } = useTestCreationContext();
    const { schoolClasses, reloadSchoolClasses, vocabTrainings, vocabTrainingsDataLoaded, vocabTrainingsLoadError } =
        useRTContext();

    const totalActivityCount = userTests.length + vocabTrainings.length;
    const isActivityDataLoaded = testDataLoaded && vocabTrainingsDataLoaded;

    useEffect(() => {
        getAndSetUserAndTeacherInfo();
        trackTouchpointPageView("assistent");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isTeacher = userInfo.visitorOrUserRole?.toLowerCase() === "teacher";

    const isDataLoaded = userInfo && typeof userInfo.userEmail !== "undefined";
    const isLoggedIn = !!userInfo.userEmail && !userInfo.isAnonymousUser;

    useEffect(() => {
        if (isTeacher) {
            reloadSchoolClasses();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTeacher]);

    // Used to build the full name by filtering out any empty/falsy values (e.g., undefined, null, "")
    const nameParts = [teacherInfo?.salutation, teacherInfo?.firstname, teacherInfo?.lastname]
        .filter(Boolean)
        .join(" ");

    const hasCompletedProfile = checkHasCompletedProfile(teacherInfo);

    const isVocabTrainingStatusKnown = hasVocabTraining !== null;
    const hasVocabTrainingResolved = hasVocabTraining === true;
    const isOnboardingComplete = hasTest && hasCompletedProfile && hasVocabTrainingResolved;
    const { animationPhase, typewriterText } = useOnboardingAnimation({
        teacherInfo,
        userInfo,
        isOnboardingComplete,
        isActivityDataLoaded,
        isVocabTrainingStatusKnown,
        hasActivityLoadError: userTestsLoadError || vocabTrainingsLoadError,
    });

    const shouldHideIntroContent =
        isLoggedIn &&
        isTeacher &&
        (animationPhase === "initial" || (isOnboardingComplete && animationPhase === "idle"));

    if (!isDataLoaded) return null; // Don't render anything if data hasn't loaded yet to prevent flickering

    return (
        <ContentWithHeader
            id={"content-with-header"}
            isAreaHeader
        >
            <div
                className="flex w-full flex-col gap-m px-s pt-xl items-center"
                style={{ maxWidth: `${process.env.REACT_APP_UPPER_GRID_STEP_BORDER}px` }}
            >
                {!shouldHideIntroContent && (
                    <AssistantIntroSection
                        animationPhase={animationPhase}
                        typewriterText={typewriterText}
                        isLoggedIn={isLoggedIn}
                        isTeacher={isTeacher}
                        isAnonymousUser={!!userInfo.isAnonymousUser}
                        nameParts={nameParts}
                        hasCreatedTest={hasTest}
                        hasCompletedProfile={hasCompletedProfile}
                        hasVocabTraining={hasVocabTrainingResolved}
                    />
                )}

                {!shouldHideIntroContent && animationPhase === "idle" && isTeacher && totalActivityCount > 0 && (
                    <hr className="w-full border-t border-p6-grey-light" />
                )}

                <DevLanguageSelector className="self-end" />

                <AnonymousHandoffFeedback />

                {isTeacher && (
                    <AssistantContentSection
                        isLoggedIn={isLoggedIn}
                        isOnboardingComplete={isOnboardingComplete}
                        schoolClasses={schoolClasses}
                        totalActivityCount={totalActivityCount}
                    />
                )}
            </div>
        </ContentWithHeader>
    );
};
